import * as React from 'react';
import {
    Box,
    Stack,
    Typography,
    TextField,
    Button,
    IconButton,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Rating,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE } from './dashboardShared';
import EditableSection from './EditableSection';
import Hero from '../components/Hero';
import AboutUs from '../components/AboutUs';
import Testimonials from '../components/Testimonials';
import Features from '../components/Features';
import MasonryImageList from '../components/MasonryImageList';
import { mergeWithDefaults } from '../context/SiteContentContext';

function makeTempId() {
    return `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function isTempId(id) {
    return id.startsWith('pending-');
}
function diffCollection(originalArr, draftArr) {
    const additions = draftArr.filter((item) => isTempId(item.id));
    const removals = originalArr.filter((o) => !draftArr.some((d) => d.id === o.id)).map((o) => o.id);
    const edits = draftArr.filter((d) => !isTempId(d.id) && originalArr.some((o) => o.id === d.id));
    return { additions, removals, edits };
}
function testimonialChanged(a, b) {
    return a.quote !== b.quote || a.author !== b.author || a.stars !== b.stars;
}
function capabilityChanged(a, b) {
    return a.title !== b.title || a.subtitle !== b.subtitle || JSON.stringify(a.specs) !== JSON.stringify(b.specs);
}
function hasCollectionChanges(originalArr, draftArr, changedFn) {
    const { additions, removals, edits } = diffCollection(originalArr, draftArr);
    return (
        additions.length > 0 ||
        removals.length > 0 ||
        edits.some((item) => changedFn(originalArr.find((o) => o.id === item.id), item))
    );
}

const RESTORE_LABELS = {
    text: 'the headline and About Us text',
    testimonials: 'testimonials',
    capabilities: 'service capabilities',
    gallery: 'the gallery',
};

// --- Text dialogs ---

function HeadlineDialog({ open, initialValue, onClose, onApply }) {
    const [value, setValue] = React.useState(initialValue);
    React.useEffect(() => {
        if (open) setValue(initialValue);
    }, [open, initialValue]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit headline</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    required
                    label="Hero headline"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => onApply({ heroHeadline: value })}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AboutDialog({ open, initialValue, onClose, onApply }) {
    const [value, setValue] = React.useState(initialValue);
    React.useEffect(() => {
        if (open) setValue(initialValue);
    }, [open, initialValue]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit About Us text</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    label="About Us text (blank line between paragraphs)"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => onApply({ aboutText: value })}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Testimonials ---

function TestimonialDialog({ open, initialValue, onClose, onApply }) {
    const [quote, setQuote] = React.useState('');
    const [author, setAuthor] = React.useState('');
    const [stars, setStars] = React.useState(5);
    React.useEffect(() => {
        if (open) {
            setQuote(initialValue?.quote || '');
            setAuthor(initialValue?.author || '');
            setStars(initialValue?.stars ?? 5);
        }
    }, [open, initialValue]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{initialValue ? 'Edit testimonial' : 'Add testimonial'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        label="Quote"
                        value={quote}
                        onChange={(e) => setQuote(e.target.value)}
                    />
                    <TextField fullWidth required label="Author" value={author} onChange={(e) => setAuthor(e.target.value)} />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Stars:</Typography>
                        <Rating value={stars} onChange={(e, v) => setStars(v || 1)} />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!quote.trim() || !author.trim()}
                    onClick={() => onApply({ quote, author, stars })}
                >
                    {initialValue ? 'Apply' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function TestimonialsManager({ testimonials, defaultsAvailable, onAdd, onEdit, onDelete, onRestore }) {
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                <Typography variant="subtitle1" sx={{ color: 'white' }}>
                    Manage testimonials
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        onClick={onRestore}
                        disabled={!defaultsAvailable}
                        title={defaultsAvailable ? undefined : 'No defaults saved yet'}
                        sx={{ color: 'grey.400' }}
                    >
                        Restore to default
                    </Button>
                    <Button size="small" variant="contained" onClick={onAdd}>
                        + Add testimonial
                    </Button>
                </Stack>
            </Stack>
            <Stack spacing={1}>
                {testimonials.map((t) => (
                    <Stack
                        key={t.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}
                    >
                        <Box sx={{ overflow: 'hidden', flex: 1 }}>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>
                                {t.author} &mdash; {t.stars}★
                            </Typography>
                            <Typography sx={{ color: 'grey.400' }} noWrap>
                                {t.quote}
                            </Typography>
                        </Box>
                        <Stack direction="row">
                            <IconButton size="small" aria-label={`Edit testimonial from ${t.author}`} onClick={() => onEdit(t)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label={`Delete testimonial from ${t.author}`} onClick={() => onDelete(t.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}

// --- Service capabilities ---

function CapabilityDialog({ open, initialValue, onClose, onApply }) {
    const [title, setTitle] = React.useState('');
    const [subtitle, setSubtitle] = React.useState('');
    const [specs, setSpecs] = React.useState([{ label: '', value: '' }]);
    const [imageFile, setImageFile] = React.useState(null);
    const isEdit = Boolean(initialValue);

    React.useEffect(() => {
        if (open) {
            setTitle(initialValue?.title || '');
            setSubtitle(initialValue?.subtitle || '');
            setSpecs(initialValue?.specs?.length ? initialValue.specs : [{ label: '', value: '' }]);
            setImageFile(null);
        }
    }, [open, initialValue]);

    const updateSpec = (index, field, value) => {
        setSpecs((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    };
    const addSpecRow = () => setSpecs((prev) => [...prev, { label: '', value: '' }]);
    const removeSpecRow = (index) => setSpecs((prev) => prev.filter((_, i) => i !== index));

    const cleanSpecs = specs.filter((s) => s.label.trim() && s.value.trim());
    const canSave = title.trim() && subtitle.trim() && cleanSpecs.length > 0 && (isEdit || imageFile);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? 'Edit' : 'Add'} service capability</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField autoFocus label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
                    <TextField
                        label="Subtitle (e.g. machine model)"
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        required
                        fullWidth
                    />
                    <Typography variant="subtitle2">Specs</Typography>
                    {specs.map((spec, i) => (
                        <Stack direction="row" spacing={1} key={i} alignItems="center">
                            <TextField
                                label="Label"
                                value={spec.label}
                                onChange={(e) => updateSpec(i, 'label', e.target.value)}
                                size="small"
                            />
                            <TextField
                                label="Value"
                                value={spec.value}
                                onChange={(e) => updateSpec(i, 'value', e.target.value)}
                                size="small"
                                fullWidth
                            />
                            <IconButton size="small" aria-label="Remove spec row" onClick={() => removeSpecRow(i)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button size="small" onClick={addSpecRow} sx={{ alignSelf: 'flex-start' }}>
                        + Add spec row
                    </Button>
                    {!isEdit && (
                        <Button variant="outlined" component="label">
                            {imageFile ? imageFile.name : 'Choose photo'}
                            <input id="capability-image" name="capabilityImage" type="file" accept="image/*" hidden onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                        </Button>
                    )}
                    {isEdit && <Alert severity="info">To change the photo, delete this capability and add a new one.</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!canSave}
                    onClick={() => onApply({ title, subtitle, specs: cleanSpecs, imageFile })}
                >
                    {isEdit ? 'Apply' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CapabilitiesManager({ capabilities, defaultsAvailable, onAdd, onEdit, onDelete, onRestore }) {
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                <Typography variant="subtitle1" sx={{ color: 'white' }}>
                    Manage service capabilities
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        onClick={onRestore}
                        disabled={!defaultsAvailable}
                        title={defaultsAvailable ? undefined : 'No defaults saved yet'}
                        sx={{ color: 'grey.400' }}
                    >
                        Restore to default
                    </Button>
                    <Button size="small" variant="contained" onClick={onAdd}>
                        + Add capability
                    </Button>
                </Stack>
            </Stack>
            <Stack spacing={1}>
                {capabilities.map((c) => (
                    <Stack
                        key={c.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: 'hidden', flex: 1 }}>
                            <img src={c.imageUrl} alt={c.title} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
                            <Box sx={{ overflow: 'hidden' }}>
                                <Typography sx={{ color: 'white', fontWeight: 600 }} noWrap>
                                    {c.title}
                                </Typography>
                                <Typography sx={{ color: 'grey.400' }} noWrap>
                                    {c.subtitle}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row">
                            <IconButton size="small" aria-label={`Edit ${c.title}`} onClick={() => onEdit(c)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label={`Delete ${c.title}`} onClick={() => onDelete(c.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}

function RestoreConfirmDialog({ open, section, onClose, onConfirm, confirming }) {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Restore to default?</DialogTitle>
            <DialogContent>
                This replaces {section ? RESTORE_LABELS[section] : 'this section'} with the original content. This
                can't be undone.
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="error" variant="contained" disabled={confirming} onClick={onConfirm}>
                    {confirming ? 'Restoring…' : 'Restore'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function EditContent({ token, onLogout }) {
    // Text
    const [original, setOriginal] = React.useState(null); // { heroHeadline, aboutText }
    const [draft, setDraft] = React.useState(null);

    // Gallery
    const [savedGallery, setSavedGallery] = React.useState([]);
    const [pendingGalleryAdds, setPendingGalleryAdds] = React.useState([]); // [{ tempId, file, previewUrl, alt }]
    const [pendingGalleryRemovals, setPendingGalleryRemovals] = React.useState([]); // [publicId]

    // Testimonials
    const [originalTestimonials, setOriginalTestimonials] = React.useState([]);
    const [draftTestimonials, setDraftTestimonials] = React.useState([]);
    const [editingTestimonial, setEditingTestimonial] = React.useState(undefined); // undefined = closed, null = adding, object = editing

    // Service capabilities
    const [originalCapabilities, setOriginalCapabilities] = React.useState([]);
    const [draftCapabilities, setDraftCapabilities] = React.useState([]);
    const [editingCapability, setEditingCapability] = React.useState(undefined);

    const [defaults, setDefaults] = React.useState(null);
    const [restoreConfirm, setRestoreConfirm] = React.useState(null); // 'text' | 'testimonials' | 'capabilities' | 'gallery' | null
    const [restoring, setRestoring] = React.useState(false);

    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState('');
    const [openDialog, setOpenDialog] = React.useState(null); // 'headline' | 'about' | null
    const [saving, setSaving] = React.useState(false);
    const [banner, setBanner] = React.useState(null); // { type, text }

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/content`);
                const data = await res.json();
                if (cancelled) return;
                const merged = mergeWithDefaults(data);
                setOriginal({ heroHeadline: merged.heroHeadline, aboutText: merged.aboutText });
                setDraft({ heroHeadline: merged.heroHeadline, aboutText: merged.aboutText });
                setSavedGallery(merged.gallery);
                setOriginalTestimonials(merged.testimonials);
                setDraftTestimonials(merged.testimonials);
                setOriginalCapabilities(merged.capabilities);
                setDraftCapabilities(merged.capabilities);
                setDefaults(data.defaults || null);
            } catch (err) {
                if (!cancelled) setLoadError('Could not load current site content.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Revoke local blob URLs on unmount. Refs so the cleanup (registered once) always
    // sees the latest pending items, not just what existed at mount time.
    const pendingGalleryAddsRef = React.useRef(pendingGalleryAdds);
    pendingGalleryAddsRef.current = pendingGalleryAdds;
    const draftCapabilitiesRef = React.useRef(draftCapabilities);
    draftCapabilitiesRef.current = draftCapabilities;
    React.useEffect(() => {
        return () => {
            pendingGalleryAddsRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
            draftCapabilitiesRef.current.forEach((c) => {
                if (c._newImageFile) URL.revokeObjectURL(c.imageUrl);
            });
        };
    }, []);

    const isDirty =
        Boolean(draft) &&
        Boolean(original) &&
        (draft.heroHeadline !== original.heroHeadline ||
            draft.aboutText !== original.aboutText ||
            pendingGalleryAdds.length > 0 ||
            pendingGalleryRemovals.length > 0 ||
            hasCollectionChanges(originalTestimonials, draftTestimonials, testimonialChanged) ||
            hasCollectionChanges(originalCapabilities, draftCapabilities, capabilityChanged));

    const applyDraft = (updates) => {
        setDraft((prev) => ({ ...prev, ...updates }));
        setOpenDialog(null);
    };

    // --- Gallery ---
    const handleAddImage = (file) => {
        const previewUrl = URL.createObjectURL(file);
        setPendingGalleryAdds((prev) => [...prev, { tempId: makeTempId(), file, previewUrl, alt: '' }]);
    };
    const handleDeleteImage = (publicId) => {
        const pending = pendingGalleryAdds.find((p) => p.tempId === publicId);
        if (pending) {
            URL.revokeObjectURL(pending.previewUrl);
            setPendingGalleryAdds((prev) => prev.filter((p) => p.tempId !== publicId));
            return;
        }
        setPendingGalleryRemovals((prev) => (prev.includes(publicId) ? prev : [...prev, publicId]));
    };

    // --- Testimonials ---
    const applyTestimonial = ({ quote, author, stars }) => {
        if (editingTestimonial) {
            const id = editingTestimonial.id;
            setDraftTestimonials((prev) => prev.map((t) => (t.id === id ? { ...t, quote, author, stars } : t)));
        } else {
            setDraftTestimonials((prev) => [...prev, { id: makeTempId(), quote, author, stars }]);
        }
        setEditingTestimonial(undefined);
    };
    const deleteTestimonial = (id) => {
        setDraftTestimonials((prev) => prev.filter((t) => t.id !== id));
    };

    // --- Service capabilities ---
    const applyCapability = ({ title, subtitle, specs, imageFile }) => {
        if (editingCapability) {
            const id = editingCapability.id;
            setDraftCapabilities((prev) => prev.map((c) => (c.id === id ? { ...c, title, subtitle, specs } : c)));
        } else {
            const previewUrl = URL.createObjectURL(imageFile);
            setDraftCapabilities((prev) => [
                ...prev,
                { id: makeTempId(), title, subtitle, specs, imageUrl: previewUrl, _newImageFile: imageFile },
            ]);
        }
        setEditingCapability(undefined);
    };
    const deleteCapability = (id) => {
        const capability = draftCapabilities.find((c) => c.id === id);
        if (capability?._newImageFile) URL.revokeObjectURL(capability.imageUrl);
        setDraftCapabilities((prev) => prev.filter((c) => c.id !== id));
    };

    // --- Discard everything ---
    const handleDiscard = () => {
        setDraft(original);
        pendingGalleryAdds.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        setPendingGalleryAdds([]);
        setPendingGalleryRemovals([]);
        setDraftTestimonials(originalTestimonials);
        draftCapabilities.forEach((c) => {
            if (c._newImageFile) URL.revokeObjectURL(c.imageUrl);
        });
        setDraftCapabilities(originalCapabilities);
        setBanner(null);
    };

    // --- Restore to default (immediate, confirmed, bypasses draft state) ---
    const handleRestore = async () => {
        const section = restoreConfirm;
        setRestoring(true);
        try {
            const res = await fetch(`${API_BASE}/api/content/${section}/restore`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 401) {
                onLogout();
                return;
            }
            const data = await res.json();
            if (!res.ok) {
                setBanner({ type: 'error', text: data.message || 'Failed to restore' });
                return;
            }
            if (section === 'text') {
                const next = { heroHeadline: data.heroHeadline, aboutText: data.aboutText };
                setOriginal(next);
                setDraft(next);
            } else if (section === 'testimonials') {
                setOriginalTestimonials(data.testimonials);
                setDraftTestimonials(data.testimonials);
            } else if (section === 'capabilities') {
                setOriginalCapabilities(data.capabilities);
                setDraftCapabilities(data.capabilities);
            } else if (section === 'gallery') {
                setSavedGallery(data.gallery);
                pendingGalleryAdds.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                setPendingGalleryAdds([]);
                setPendingGalleryRemovals([]);
            }
            setBanner({ type: 'success', text: 'Restored to default.' });
        } catch (err) {
            setBanner({ type: 'error', text: 'Could not reach the server. Please try again.' });
        } finally {
            setRestoring(false);
            setRestoreConfirm(null);
        }
    };

    // --- Save everything ---
    const handleSaveAll = async () => {
        setSaving(true);
        setBanner(null);
        const failures = [];
        const authHeaders = { Authorization: `Bearer ${token}` };
        let loggedOut = false;
        const checkAuth = (res) => {
            if (res.status === 401) {
                onLogout();
                loggedOut = true;
                return true;
            }
            return false;
        };

        // 1. Text
        try {
            const res = await fetch(`${API_BASE}/api/content`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: JSON.stringify(draft),
            });
            if (checkAuth(res)) return setSaving(false);
            if (!res.ok) failures.push('site text');
        } catch (err) {
            failures.push('site text');
        }

        // 2. Gallery removals
        let latestGallery = savedGallery;
        if (!loggedOut) {
            for (const publicId of pendingGalleryRemovals) {
                try {
                    const res = await fetch(`${API_BASE}/api/content/gallery/${encodeURIComponent(publicId)}`, {
                        method: 'DELETE',
                        headers: authHeaders,
                    });
                    if (checkAuth(res)) break;
                    if (res.ok) {
                        latestGallery = (await res.json()).gallery;
                    } else {
                        failures.push('remove a photo');
                    }
                } catch (err) {
                    failures.push('remove a photo');
                }
            }
        }

        // 3. Gallery additions
        const stillPendingGalleryAdds = [];
        if (!loggedOut) {
            for (const p of pendingGalleryAdds) {
                try {
                    const formData = new FormData();
                    formData.append('image', p.file);
                    formData.append('alt', p.alt || '');
                    const res = await fetch(`${API_BASE}/api/content/gallery`, {
                        method: 'POST',
                        headers: authHeaders,
                        body: formData,
                    });
                    if (checkAuth(res)) {
                        stillPendingGalleryAdds.push(p);
                        continue;
                    }
                    if (res.ok) {
                        latestGallery = (await res.json()).gallery;
                        URL.revokeObjectURL(p.previewUrl);
                    } else {
                        failures.push('upload a photo');
                        stillPendingGalleryAdds.push(p);
                    }
                } catch (err) {
                    failures.push('upload a photo');
                    stillPendingGalleryAdds.push(p);
                }
            }
        }

        // 4. Testimonials
        let latestTestimonials = originalTestimonials;
        if (!loggedOut) {
            const { additions, removals, edits } = diffCollection(originalTestimonials, draftTestimonials);
            for (const id of removals) {
                try {
                    const res = await fetch(`${API_BASE}/api/content/testimonials/${id}`, {
                        method: 'DELETE',
                        headers: authHeaders,
                    });
                    if (checkAuth(res)) break;
                    if (res.ok) latestTestimonials = (await res.json()).testimonials;
                    else failures.push('remove a testimonial');
                } catch (err) {
                    failures.push('remove a testimonial');
                }
            }
            for (const t of edits) {
                if (loggedOut) break;
                const prevItem = originalTestimonials.find((o) => o.id === t.id);
                if (!testimonialChanged(prevItem, t)) continue;
                try {
                    const res = await fetch(`${API_BASE}/api/content/testimonials/${t.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', ...authHeaders },
                        body: JSON.stringify({ quote: t.quote, author: t.author, stars: t.stars }),
                    });
                    if (checkAuth(res)) break;
                    if (res.ok) latestTestimonials = (await res.json()).testimonials;
                    else failures.push('update a testimonial');
                } catch (err) {
                    failures.push('update a testimonial');
                }
            }
            for (const t of additions) {
                if (loggedOut) break;
                try {
                    const res = await fetch(`${API_BASE}/api/content/testimonials`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authHeaders },
                        body: JSON.stringify({ quote: t.quote, author: t.author, stars: t.stars }),
                    });
                    if (checkAuth(res)) break;
                    if (res.ok) latestTestimonials = (await res.json()).testimonials;
                    else failures.push('add a testimonial');
                } catch (err) {
                    failures.push('add a testimonial');
                }
            }
        }

        // 5. Service capabilities
        let latestCapabilities = originalCapabilities;
        const stillPendingCapabilityAdds = [];
        if (!loggedOut) {
            const { additions, removals, edits } = diffCollection(originalCapabilities, draftCapabilities);
            for (const id of removals) {
                try {
                    const res = await fetch(`${API_BASE}/api/content/capabilities/${encodeURIComponent(id)}`, {
                        method: 'DELETE',
                        headers: authHeaders,
                    });
                    if (checkAuth(res)) break;
                    if (res.ok) latestCapabilities = (await res.json()).capabilities;
                    else failures.push('remove a service capability');
                } catch (err) {
                    failures.push('remove a service capability');
                }
            }
            for (const c of edits) {
                if (loggedOut) break;
                const prevItem = originalCapabilities.find((o) => o.id === c.id);
                if (!capabilityChanged(prevItem, c)) continue;
                try {
                    const res = await fetch(`${API_BASE}/api/content/capabilities/${encodeURIComponent(c.id)}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', ...authHeaders },
                        body: JSON.stringify({ title: c.title, subtitle: c.subtitle, specs: c.specs }),
                    });
                    if (checkAuth(res)) break;
                    if (res.ok) latestCapabilities = (await res.json()).capabilities;
                    else failures.push('update a service capability');
                } catch (err) {
                    failures.push('update a service capability');
                }
            }
            for (const c of additions) {
                if (loggedOut) {
                    stillPendingCapabilityAdds.push(c);
                    continue;
                }
                try {
                    const formData = new FormData();
                    formData.append('image', c._newImageFile);
                    formData.append('title', c.title);
                    formData.append('subtitle', c.subtitle);
                    formData.append('specs', JSON.stringify(c.specs));
                    const res = await fetch(`${API_BASE}/api/content/capabilities`, {
                        method: 'POST',
                        headers: authHeaders,
                        body: formData,
                    });
                    if (checkAuth(res)) {
                        stillPendingCapabilityAdds.push(c);
                        continue;
                    }
                    if (res.ok) {
                        latestCapabilities = (await res.json()).capabilities;
                        URL.revokeObjectURL(c.imageUrl);
                    } else {
                        failures.push('add a service capability');
                        stillPendingCapabilityAdds.push(c);
                    }
                } catch (err) {
                    failures.push('add a service capability');
                    stillPendingCapabilityAdds.push(c);
                }
            }
        }

        if (loggedOut) {
            setSaving(false);
            return;
        }

        setSavedGallery(latestGallery);
        setPendingGalleryAdds(stillPendingGalleryAdds);
        setOriginalTestimonials(latestTestimonials);
        setDraftTestimonials(latestTestimonials);
        setOriginalCapabilities(latestCapabilities);
        setDraftCapabilities(
            stillPendingCapabilityAdds.length ? [...latestCapabilities, ...stillPendingCapabilityAdds] : latestCapabilities
        );

        if (failures.length === 0) {
            setOriginal(draft);
            setPendingGalleryRemovals([]);
            setBanner({ type: 'success', text: 'Saved — changes are live on the site.' });
        } else {
            setPendingGalleryRemovals((prev) => prev.filter((id) => latestGallery.some((img) => img.publicId === id)));
            setBanner({
                type: 'error',
                text: `Couldn't save: ${[...new Set(failures)].join(', ')}. Everything else was saved — try again for the rest.`,
            });
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    const displayedGallery = [
        ...savedGallery
            .filter((img) => !pendingGalleryRemovals.includes(img.publicId))
            .map((img) => ({ ...img, pending: false })),
        ...pendingGalleryAdds.map((p) => ({
            publicId: p.tempId,
            url: p.previewUrl,
            alt: p.alt,
            pending: true,
        })),
    ];

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                This is a live preview of your website. Hover a section (or tap, on mobile) and click the pencil
                to edit it. Nothing goes live until you click "Save changes" below.
            </Alert>
            {banner && (
                <Alert severity={banner.type} sx={{ mb: 2 }}>
                    {banner.text}
                </Alert>
            )}

            {isDirty && (
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,193,7,0.12)',
                        border: '1px solid rgba(255,193,7,0.4)',
                    }}
                >
                    <Typography sx={{ color: 'white' }}>You have unsaved changes.</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={handleDiscard} disabled={saving} sx={{ color: 'grey.400' }}>
                            Discard
                        </Button>
                        <Button variant="contained" onClick={handleSaveAll} disabled={saving}>
                            {saving ? 'Saving…' : 'Save changes'}
                        </Button>
                    </Stack>
                </Stack>
            )}

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <EditableSection
                    label="headline"
                    onEdit={() => setOpenDialog('headline')}
                    onRestore={() => setRestoreConfirm('text')}
                    restoreDisabled={!defaults}
                >
                    <Hero headline={draft.heroHeadline} />
                </EditableSection>
                <EditableSection
                    label="about text"
                    onEdit={() => setOpenDialog('about')}
                    onRestore={() => setRestoreConfirm('text')}
                    restoreDisabled={!defaults}
                >
                    <AboutUs aboutText={draft.aboutText} />
                </EditableSection>

                <Testimonials testimonials={draftTestimonials} />
                <TestimonialsManager
                    testimonials={draftTestimonials}
                    defaultsAvailable={Boolean(defaults)}
                    onAdd={() => setEditingTestimonial(null)}
                    onEdit={(t) => setEditingTestimonial(t)}
                    onDelete={deleteTestimonial}
                    onRestore={() => setRestoreConfirm('testimonials')}
                />

                <MasonryImageList
                    gallery={displayedGallery}
                    editable
                    onAddImage={handleAddImage}
                    onDeleteImage={handleDeleteImage}
                />
                <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pb: 2 }}>
                    <Button
                        size="small"
                        onClick={() => setRestoreConfirm('gallery')}
                        disabled={!defaults}
                        title={defaults ? undefined : 'No defaults saved yet'}
                    >
                        Restore gallery to default
                    </Button>
                </Stack>

                <Features capabilities={draftCapabilities} />
                <CapabilitiesManager
                    capabilities={draftCapabilities}
                    defaultsAvailable={Boolean(defaults)}
                    onAdd={() => setEditingCapability(null)}
                    onEdit={(c) => setEditingCapability(c)}
                    onDelete={deleteCapability}
                    onRestore={() => setRestoreConfirm('capabilities')}
                />
            </Box>

            <HeadlineDialog
                open={openDialog === 'headline'}
                initialValue={draft.heroHeadline}
                onClose={() => setOpenDialog(null)}
                onApply={applyDraft}
            />
            <AboutDialog
                open={openDialog === 'about'}
                initialValue={draft.aboutText}
                onClose={() => setOpenDialog(null)}
                onApply={applyDraft}
            />
            <TestimonialDialog
                open={editingTestimonial !== undefined}
                initialValue={editingTestimonial}
                onClose={() => setEditingTestimonial(undefined)}
                onApply={applyTestimonial}
            />
            <CapabilityDialog
                open={editingCapability !== undefined}
                initialValue={editingCapability}
                onClose={() => setEditingCapability(undefined)}
                onApply={applyCapability}
            />
            <RestoreConfirmDialog
                open={Boolean(restoreConfirm)}
                section={restoreConfirm}
                confirming={restoring}
                onClose={() => setRestoreConfirm(null)}
                onConfirm={handleRestore}
            />
        </Box>
    );
}
